import React, { useState, useEffect } from 'react';
// import { supabase } from '../../../lib/supabase'; // Not used directly anymore
import { api } from '../../../services/api';
import { Review, ReviewStatus, UserProfile } from '../../../types';
import { 
  Star, Check, X, MessageSquare, Calendar, Edit2, Trash2, 
  CheckSquare, Square, Filter, ChevronLeft, ChevronRight, Image as ImageIcon 
} from 'lucide-react';

interface ModerationListProps {
  user: UserProfile;
}

export const ModerationList: React.FC<ModerationListProps> = ({ user }) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  
  // --- Pagination & Filter State ---
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [ratingFilter, setRatingFilter] = useState<number | null>(null);
  const [mediaFilter, setMediaFilter] = useState<boolean>(false); // false = All, true = With Photos
  
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20); // Items per page
  const [totalReviews, setTotalReviews] = useState(0);
  
  // Tab Counts
  const [counts, setCounts] = useState({ all: 0, pending: 0, approved: 0, rejected: 0 });

  // --- Processing State ---
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Review>>({});
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  
  // Batch selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchProcessing, setBatchProcessing] = useState(false);

  // 1. Fetch Tab Counts (Runs on mount & after updates)
  const loadCounts = async () => {
    try {
      const countsData = await api.getReviewCounts(user.id);
      setCounts(countsData);
    } catch (err) {
      console.error("Failed to load counts", err);
    }
  };

  // 2. Fetch Reviews (Runs when filters/page changes)
  const loadReviews = async () => {
    try {
      setLoading(true);
      const { data, total } = await api.getReviewsPaginated(user.id, page, pageSize, {
        status: statusFilter,
        rating: ratingFilter,
        hasMedia: mediaFilter
      });
      
      setReviews(data);
      setTotalReviews(total);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial Load & Real-time setup
  useEffect(() => {
    loadCounts();
    loadReviews();

    // Real-time subscription
    const subscription = api.subscribeToReviews?.(user.id, (payload) => {
      // On any change, reload counts and current page data to stay fresh
      // (Optimistic updates are complex with pagination, refreshing is safer)
      loadCounts();
      loadReviews(); 
    });

    return () => {
      if (subscription?.unsubscribe) subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, statusFilter, ratingFilter, mediaFilter, page]); 
  // Reload when any filter or page changes

  // --- Handlers ---

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= Math.ceil(totalReviews / pageSize)) {
      setPage(newPage);
      setSelectedIds(new Set()); // Clear selection on page change
    }
  };

  // Toggle single selection
  const toggleSelection = (reviewId: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) newSet.delete(reviewId);
      else newSet.add(reviewId);
      return newSet;
    });
  };

  // Toggle select all (Current Page Only)
  const toggleSelectAll = () => {
    if (selectedIds.size === reviews.length && reviews.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(reviews.map(r => r.id)));
    }
  };

  // Helper for batch actions to refresh UI
  const onBatchComplete = () => {
    setSelectedIds(new Set());
    setBatchProcessing(false);
    loadReviews();
    loadCounts();
  };

  const handleBatchApprove = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Approve ${selectedIds.size} reviews?`)) return;
    setBatchProcessing(true);
    try {
      await Promise.all(Array.from(selectedIds).map(id => api.updateReviewStatus(id, 'approved')));
      alert(`Approved ${selectedIds.size} reviews!`);
      onBatchComplete();
    } catch (e) { alert('Batch failed'); setBatchProcessing(false); }
  };

  const handleBatchReject = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Reject ${selectedIds.size} reviews?`)) return;
    setBatchProcessing(true);
    try {
      await Promise.all(Array.from(selectedIds).map(id => api.updateReviewStatus(id, 'rejected')));
      alert(`Rejected ${selectedIds.size} reviews!`);
      onBatchComplete();
    } catch (e) { alert('Batch failed'); setBatchProcessing(false); }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`⚠️ DELETE ${selectedIds.size} reviews? Irreversible!`)) return;
    setBatchProcessing(true);
    try {
      await Promise.all(Array.from(selectedIds).map(id => api.deleteReview?.(id)));
      alert(`Deleted ${selectedIds.size} reviews!`);
      onBatchComplete();
    } catch (e) { alert('Batch failed'); setBatchProcessing(false); }
  };

  // Single Actions
  const handleSingleAction = async (id: string, action: () => Promise<any>) => {
    setProcessingIds(prev => new Set(prev).add(id));
    try {
      await action();
      // Refresh list to maintain correct order/pagination
      loadReviews(); 
      loadCounts();
    } catch (e) { console.error(e); }
    finally {
      setProcessingIds(prev => { const s = new Set(prev); s.delete(id); return s; });
    }
  };

  // Helper to Render stars
  const renderStars = (rating: number) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <Star key={star} className={`w-4 h-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
      ))}
    </div>
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const totalPages = Math.ceil(totalReviews / pageSize);

  return (
    <div className="space-y-4">
      
      {/* 1. STATUS TABS */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200">
        {(['pending', 'approved', 'rejected', 'all'] as const).map(f => (
          <button
            key={f}
            onClick={() => {
              setStatusFilter(f);
              setPage(1); // Reset to page 1 on filter change
              setSelectedIds(new Set());
            }}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              statusFilter === f
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${statusFilter === f ? 'bg-blue-100 text-blue-700' : 'bg-gray-100'}`}>
              {counts[f]}
            </span>
          </button>
        ))}
      </div>

      {/* 2. ADVANCED FILTERS BAR */}
      <div className="flex flex-wrap items-center gap-4 bg-gray-50 p-3 rounded-lg border border-gray-200">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Filter className="w-4 h-4" />
          <span className="font-medium">Filters:</span>
        </div>

        {/* Rating Filter */}
        <select 
          value={ratingFilter || ''} 
          onChange={(e) => {
            setRatingFilter(e.target.value ? parseInt(e.target.value) : null);
            setPage(1);
          }}
          className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All Stars</option>
          <option value="5">5 Stars</option>
          <option value="4">4 Stars</option>
          <option value="3">3 Stars</option>
          <option value="2">2 Stars</option>
          <option value="1">1 Star</option>
        </select>

        {/* Media Filter */}
        <button
          onClick={() => {
            setMediaFilter(!mediaFilter);
            setPage(1);
          }}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm border transition-colors ${
            mediaFilter 
              ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700' 
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          <ImageIcon className="w-4 h-4" />
          {mediaFilter ? 'With Photos Only' : 'All Reviews'}
        </button>

        {/* Results Count */}
        <div className="ml-auto text-sm text-gray-500">
          Showing {reviews.length} of {totalReviews}
        </div>
      </div>

      {/* 3. BATCH ACTIONS (Only Visible when selected) */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-3 animate-in fade-in slide-in-from-top-2">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-blue-900">
              {selectedIds.size} selected
            </span>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Clear
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <button onClick={handleBatchApprove} disabled={batchProcessing} className="btn-batch bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1">
              <Check className="w-4 h-4" /> Approve
            </button>
            <button onClick={handleBatchReject} disabled={batchProcessing} className="btn-batch bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1">
              <X className="w-4 h-4" /> Reject
            </button>
            <button onClick={handleBatchDelete} disabled={batchProcessing} className="btn-batch bg-gray-800 hover:bg-gray-900 text-white px-3 py-1.5 rounded text-sm flex items-center gap-1">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          </div>
        </div>
      )}

      {/* 4. REVIEWS LIST */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-white rounded-lg border border-dashed border-gray-300">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="text-lg font-medium">No reviews found</p>
          <p className="text-sm">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          {/* Header */}
          <div className="grid grid-cols-12 gap-4 bg-gray-50 border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">
            <div className="col-span-1">
              <button onClick={toggleSelectAll} className="hover:bg-gray-200 p-1 rounded">
                {selectedIds.size === reviews.length ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5 text-gray-400" />}
              </button>
            </div>
            <div className="col-span-3">Product</div>
            <div className="col-span-2">Reviewer</div>
            <div className="col-span-3">Content</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          {/* Rows */}
          <div className="divide-y divide-gray-200">
            {reviews.map(review => {
              const isEditing = editingId === review.id;
              const isReplying = replyingTo === review.id;
              const isSelected = selectedIds.has(review.id);
              const isProcessing = processingIds.has(review.id);

              return (
                <div key={review.id} className={`grid grid-cols-12 gap-4 px-4 py-4 transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                  {/* Checkbox */}
                  <div className="col-span-1 pt-1">
                    <button onClick={() => toggleSelection(review.id)} className="hover:bg-gray-200 p-1 rounded">
                      {isSelected ? <CheckSquare className="w-5 h-5 text-blue-600" /> : <Square className="w-5 h-5 text-gray-400" />}
                    </button>
                  </div>

                  {/* Product */}
                  <div className="col-span-3">
                    <div className="flex items-center gap-3">
                      {review.media_urls?.[0] ? (
                        <img src={review.media_urls[0].url} alt="" className="w-12 h-12 object-cover rounded border" />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded border flex items-center justify-center text-xs text-gray-400">No Img</div>
                      )}
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {review.product_id?.split('-').join(' ') || 'Unknown Product'}
                      </div>
                    </div>
                  </div>

                  {/* Reviewer */}
                  <div className="col-span-2">
                    {isEditing ? (
                      <input 
                        className="w-full border rounded px-2 py-1 text-sm" 
                        value={editForm.reviewer_name || ''} 
                        onChange={e => setEditForm({...editForm, reviewer_name: e.target.value})}
                      />
                    ) : (
                      <>
                        <div className="font-medium text-sm text-gray-900">{review.reviewer_name}</div>
                        <div className="mt-1">{renderStars(review.rating)}</div>
                        {review.verified_purchase && <span className="text-[10px] bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full mt-1 inline-block">Verified</span>}
                      </>
                    )}
                  </div>

                  {/* Content */}
                  <div className="col-span-3 text-sm text-gray-600">
                    {isEditing ? (
                      <div className="space-y-2">
                        <select 
                          className="w-full border rounded p-1"
                          value={editForm.rating || review.rating}
                          onChange={e => setEditForm({...editForm, rating: parseInt(e.target.value)})}
                        >
                          {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} Stars</option>)}
                        </select>
                        <textarea 
                          className="w-full border rounded p-1" 
                          rows={3}
                          value={editForm.content || ''}
                          onChange={e => setEditForm({...editForm, content: e.target.value})}
                        />
                      </div>
                    ) : (
                      <>
                        <p className="line-clamp-3">{review.content}</p>
                        {review.media_urls && review.media_urls.length > 0 && (
                          <div className="flex gap-1 mt-2">
                             <ImageIcon className="w-3 h-3 text-gray-400" />
                             <span className="text-xs text-gray-500">{review.media_urls.length} photos</span>
                          </div>
                        )}
                        {/* Reply UI */}
                        {isReplying && (
                          <div className="mt-2 bg-gray-50 p-2 rounded border">
                            <textarea 
                              className="w-full border rounded p-1 text-sm mb-2" 
                              placeholder="Reply..." 
                              autoFocus
                              value={replyText}
                              onChange={e => setReplyText(e.target.value)}
                            />
                            <div className="flex gap-2">
                              <button onClick={() => handleSingleAction(review.id, async () => {
                                await api.addReviewResponse?.(review.id, replyText);
                                setReplyingTo(null); setReplyText('');
                              })} className="px-2 py-1 bg-blue-600 text-white text-xs rounded">Send</button>
                              <button onClick={() => setReplyingTo(null)} className="px-2 py-1 bg-gray-200 text-xs rounded">Cancel</button>
                            </div>
                          </div>
                        )}
                        {review.response_text && !isReplying && !isEditing && (
                           <div className="mt-2 text-xs bg-blue-50 text-blue-800 p-2 rounded">
                             <span className="font-bold">Reply:</span> {review.response_text}
                           </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Date */}
                  <div className="col-span-2 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {formatDate(review.created_at)}
                    </div>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] mt-1 font-medium capitalize 
                      ${review.status === 'approved' ? 'bg-green-100 text-green-800' : 
                        review.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {review.status}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex flex-col items-end gap-1">
                     {isEditing ? (
                        <>
                          <button onClick={() => handleSingleAction(review.id, async () => {
                             await api.updateReview?.(review.id, editForm);
                             setEditingId(null);
                          })} className="text-xs bg-green-600 text-white px-2 py-1 rounded w-full">Save</button>
                          <button onClick={() => setEditingId(null)} className="text-xs bg-gray-300 px-2 py-1 rounded w-full">Cancel</button>
                        </>
                     ) : (
                       <>
                         {review.status === 'pending' && (
                           <>
                             <button disabled={isProcessing} onClick={() => handleSingleAction(review.id, () => api.updateReviewStatus(review.id, 'approved'))} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Approve"><Check className="w-4 h-4"/></button>
                             <button disabled={isProcessing} onClick={() => handleSingleAction(review.id, () => api.updateReviewStatus(review.id, 'rejected'))} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Reject"><X className="w-4 h-4"/></button>
                           </>
                         )}
                         <div className="flex gap-1 mt-1">
                           <button onClick={() => { setEditingId(review.id); setEditForm(review); }} className="text-gray-400 hover:text-blue-600"><Edit2 className="w-4 h-4"/></button>
                           <button onClick={() => { setReplyingTo(review.id); setReplyText(review.response_text || ''); }} className="text-gray-400 hover:text-purple-600"><MessageSquare className="w-4 h-4"/></button>
                           <button onClick={() => handleSingleAction(review.id, () => api.deleteReview?.(review.id))} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                         </div>
                       </>
                     )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 5. PAGINATION CONTROLS */}
      {totalReviews > 0 && (
        <div className="flex items-center justify-between bg-white px-4 py-3 border border-gray-200 rounded-lg shadow-sm">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{Math.min((page - 1) * pageSize + 1, totalReviews)}</span> to <span className="font-medium">{Math.min(page * pageSize, totalReviews)}</span> of <span className="font-medium">{totalReviews}</span> results
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            
            <div className="flex items-center gap-1">
               {/* Simple page numbers */}
               {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  // Logic to keep current page centered if possible
                  let p = i + 1;
                  if (totalPages > 5 && page > 3) p = page - 2 + i;
                  if (p > totalPages) p = i + 1 + (totalPages - 5); 
                  
                  return (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={`w-8 h-8 flex items-center justify-center rounded-md text-sm ${
                        page === p ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {p}
                    </button>
                  );
               })}
            </div>

            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
              className="flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
